/**
 * Blood Bowl Probability Calculator
 *
 * Main calculator page component that orchestrates all sections.
 */

import { useEffect, useRef, useMemo } from "react";
import { cn } from "@/lib/utils";
import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { useCalculatorState, useProbabilityCalculation } from "./hooks";
import { TeamRerolls } from "./components/TeamRerolls";
import { PlayersSection } from "./components/PlayersSection";
import { ActionsSection } from "./components/ActionsSection";
import { ResultsSection } from "./components/ResultsSection";
import { ComparisonView } from "./components/ComparisonView";
import { LanguageProvider, useLanguage } from "./i18n";
import type { ComparisonChoice } from "./types";

function CalculatorContent() {
  const { t } = useLanguage();
  const {
    state,
    setTeamRerolls,
    addPlayer,
    removePlayer,
    updatePlayerNumber,
    toggleSkill,
    setLoner,
    setLonerValue,
    addActionGroup,
    removeActionGroup,
    setGroupPlayer,
    addAction,
    removeAction,
    updateActionType,
    updateActionResult,
    updateActionReroll,
    setResults,
    // Comparison mode
    toggleComparisonMode,
    setActiveChoice,
    setChoiceResults,
  } = useCalculatorState();

  const { calculate } = useProbabilityCalculation();
  const resultsRef = useRef<HTMLDivElement>(null);
  const prevHadResults = useRef(false);

  // Initialize with default player and action group
  useEffect(() => {
    if (Object.keys(state.players).length === 0) {
      addPlayer();
    }
    if (state.actionGroups.length === 0) {
      addActionGroup();
    }
  }, []);

  // Real-time probability calculation
  const calculatedResults = useMemo(() => {
    const hasActions = state.actionGroups.some((g) => g.actions.length > 0);
    if (!hasActions) {
      return null;
    }
    return calculate(state);
  }, [
    state.teamRerolls,
    state.players,
    state.actionGroups,
    calculate,
  ]);

  // Update results in state when calculation changes
  useEffect(() => {
    setResults(calculatedResults);
  }, [calculatedResults, setResults]);

  // In comparison mode, also update the active choice's results
  useEffect(() => {
    if (state.comparisonMode) {
      setChoiceResults(state.activeChoiceIndex, calculatedResults);
    }
  }, [calculatedResults, state.comparisonMode, state.activeChoiceIndex, setChoiceResults]);

  // Get the synced choices (with current actionGroups synced to active choice)
  const syncedChoices = useMemo((): [ComparisonChoice, ComparisonChoice] => {
    if (!state.comparisonMode) return state.choices;
    const choices = [...state.choices] as [ComparisonChoice, ComparisonChoice];
    choices[state.activeChoiceIndex] = {
      ...choices[state.activeChoiceIndex],
      actionGroups: state.actionGroups,
      results: state.results,
    };
    return choices;
  }, [state.comparisonMode, state.choices, state.activeChoiceIndex, state.actionGroups, state.results]);

  // Auto-scroll to results when they first appear
  useEffect(() => {
    if (calculatedResults && !prevHadResults.current) {
      // Small delay to ensure DOM has updated
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
    prevHadResults.current = !!calculatedResults;
  }, [calculatedResults]);

  const usedRerolls = state.results?.teamRerollsUsed ?? 0;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#f5f0e6] font-body">
      {/* Background overlay */}
      <div
        className={cn(
          "fixed inset-0 pointer-events-none z-0",
          "bg-[repeating-linear-gradient(0deg,transparent,transparent_2px,rgba(139,0,0,0.03)_2px,rgba(139,0,0,0.03)_4px)]",
          "[background-image:repeating-linear-gradient(0deg,transparent,transparent_2px,rgba(139,0,0,0.03)_2px,rgba(139,0,0,0.03)_4px),radial-gradient(ellipse_at_50%_0%,rgba(139,0,0,0.15)_0%,transparent_60%)]"
        )}
      />

      <Header />

      <main className="relative z-10 max-w-4xl mx-auto px-4 py-8">
        {/* Comparison Mode Toggle */}
        <div className="flex justify-end mb-4">
          <Button
            onClick={toggleComparisonMode}
            variant={state.comparisonMode ? "default" : "outline"}
            size="sm"
            className={cn(
              "font-condensed uppercase tracking-wider",
              state.comparisonMode
                ? "bg-bb-blood-red hover:bg-bb-blood-bright"
                : "text-bb-black border-bb-bone-white/50 hover:text-bb-blood-red"
            )}
          >
            {state.comparisonMode ? t.comparison.exitMode : t.comparison.enterMode}
          </Button>
        </div>

        {/* Comparison View - shown when in comparison mode */}
        {state.comparisonMode && (
          <ComparisonView
            choices={syncedChoices}
            activeChoiceIndex={state.activeChoiceIndex}
            teamRerollsAvailable={state.teamRerolls}
            onSelectChoice={setActiveChoice}
          />
        )}

        {/* Team Rerolls */}
        <TeamRerolls
          value={state.teamRerolls}
          usedCount={usedRerolls}
          onChange={setTeamRerolls}
          className="mb-6"
        />

        {/* Players Section */}
        <PlayersSection
          players={state.players}
          onAddPlayer={addPlayer}
          onRemovePlayer={removePlayer}
          onUpdatePlayerNumber={updatePlayerNumber}
          onToggleSkill={toggleSkill}
          onSetLoner={setLoner}
          onSetLonerValue={setLonerValue}
        />

        {/* Actions Section - with label in comparison mode */}
        {state.comparisonMode && (
          <div className="mb-2 text-sm text-bb-gold/70">
            {t.comparison.editingChoice}: <span className="font-bold text-bb-gold">{syncedChoices[state.activeChoiceIndex].label}</span>
          </div>
        )}
        <ActionsSection
          groups={state.actionGroups}
          players={state.players}
          onAddGroup={addActionGroup}
          onRemoveGroup={removeActionGroup}
          onSetGroupPlayer={setGroupPlayer}
          onAddAction={addAction}
          onRemoveAction={removeAction}
          onUpdateActionType={updateActionType}
          onUpdateActionResult={updateActionResult}
          onUpdateActionReroll={updateActionReroll}
        />

        {/* Results Section - Only show in non-comparison mode */}
        {!state.comparisonMode && state.results && (
          <div ref={resultsRef}>
            <ResultsSection
              results={state.results}
              teamRerollsAvailable={state.teamRerolls}
            />
          </div>
        )}
      </main>
    </div>
  );
}

export function Calculator() {
  return (
    <LanguageProvider>
      <CalculatorContent />
    </LanguageProvider>
  );
}
